import { useState } from 'react';
import {
  makeStyles, tokens, Spinner, Text, Dropdown, Option, Field, Input, Button,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { AddRegular, CopyRegular, DeleteRegular } from '@fluentui/react-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useResetOnOpen } from '../../hooks/useResetOnOpen';
import { useRanchoStore } from '../../store/rancho.store';
import { useT } from '../../i18n';
import {
  createProtocol, updateProtocol, type MarcoType, type ProtocolRecord,
} from '../../api/protocol.api';
import {
  DEFAULT_UNIT, emptyFertForm, emptyFertRow, emptyPulvApp, fertFromForm, fertToForm, newColumnKey,
  pulvFromForm, pulvToForm, type FertForm, type MarcoFormError, type PulvFormApp,
} from './marco-form';

const useStyles = makeStyles({
  scroll: { overflowX: 'auto', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  table:  { borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' },
  th:     { padding: tokens.spacingVerticalXS, verticalAlign: 'bottom', textAlign: 'left', backgroundColor: tokens.colorNeutralBackground2, whiteSpace: 'nowrap' },
  td:     { padding: tokens.spacingVerticalXXS, borderTop: `1px solid ${tokens.colorNeutralStroke3}` },
  num:    { width: '84px' },
  wide:   { width: '170px' },
  hint:   { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  card:   { border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, padding: tokens.spacingVerticalM, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
});

interface Props {
  open:     boolean;
  onClose:  () => void;
  initial?: ProtocolRecord | null;
}

export function MarcoEditorDialog({ open, onClose, initial }: Props) {
  const t  = useT();
  const pt = t.protocol;
  const s  = useStyles();
  const qc = useQueryClient();
  const isEdit = !!initial;
  const activeRanchoId = useRanchoStore((st) => st.activeRanchoId);

  const initKind = (initial?.type === 'MARCO_PULVERIZACION' ? 'MARCO_PULVERIZACION' : 'MARCO_FERTILIZACION') as MarcoType;
  const [kind, setKind]     = useState<MarcoType>(initKind);
  const [nombre, setNombre] = useState(initial?.nombre ?? '');
  const [crop, setCrop]     = useState(initial?.crop ?? '');
  const [ciclo, setCiclo]   = useState(initial?.ciclo ?? '');
  const [fert, setFert]     = useState<FertForm>(() => initial?.marcoFertilizacion ? fertToForm(initial.marcoFertilizacion) : emptyFertForm());
  const [pulv, setPulv]     = useState<PulvFormApp[]>(() => pulvToForm(initial?.type === 'MARCO_PULVERIZACION' ? initial.aplicaciones : undefined));
  const [error, setError]   = useState('');

  useResetOnOpen(open, () => {
    setKind(initKind);
    setNombre(initial?.nombre ?? '');
    setCrop(initial?.crop ?? '');
    setCiclo(initial?.ciclo ?? '');
    setFert(initial?.marcoFertilizacion ? fertToForm(initial.marcoFertilizacion) : emptyFertForm());
    setPulv(pulvToForm(initial?.type === 'MARCO_PULVERIZACION' ? initial.aplicaciones : undefined));
    setError('');
  });

  const errorText: Record<MarcoFormError, string> = {
    columns: pt.marcoColumnsRequired, rows: pt.marcoRowsRequired, dias: pt.marcoDiasInvalid, cantidad: pt.marcoCantidadInvalid,
  };

  const mut = useMutation({
    mutationFn: () => {
      const base = { nombre: nombre.trim(), crop: crop.trim() || undefined, ciclo: ciclo.trim() || undefined };
      if (kind === 'MARCO_FERTILIZACION') {
        const r = fertFromForm(fert);
        if (r.error) throw new Error(errorText[r.error]);
        return isEdit
          ? updateProtocol(initial!.protocolId, { ...base, marcoFertilizacion: r.value })
          : createProtocol({ ...base, type: kind, aplicaciones: [], ranchoId: activeRanchoId || undefined, marcoFertilizacion: r.value });
      }
      const r = pulvFromForm(pulv);
      if (r.error) throw new Error(errorText[r.error]);
      return isEdit
        ? updateProtocol(initial!.protocolId, { ...base, aplicaciones: r.value })
        : createProtocol({ ...base, type: kind, ranchoId: activeRanchoId || undefined, aplicaciones: r.value });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['protocols'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? pt.marcoSaveError),
  });

  const submit = () => {
    setError('');
    if (!nombre.trim()) { setError(pt.marcoNameRequired); return; }
    mut.mutate();
  };

  const busy = mut.isPending;

  // ── Fertilización ──
  const setCol = (i: number, patch: Partial<FertForm['columnas'][number]>) =>
    setFert(f => ({ ...f, columnas: f.columnas.map((c, j) => j === i ? { ...c, ...patch } : c) }));
  const removeCol = (i: number) =>
    setFert(f => ({ ...f, columnas: f.columnas.filter((_, j) => j !== i) }));
  const setRow = (i: number, patch: Partial<FertForm['filas'][number]>) =>
    setFert(f => ({ ...f, filas: f.filas.map((r, j) => j === i ? { ...r, ...patch } : r) }));
  const setCell = (i: number, key: string, value: string) =>
    setFert(f => ({ ...f, filas: f.filas.map((r, j) => j === i ? { ...r, cantidades: { ...r.cantidades, [key]: value } } : r) }));
  const duplicateRow = (i: number) =>
    setFert(f => ({ ...f, filas: [...f.filas.slice(0, i + 1), { ...f.filas[i], cantidades: { ...f.filas[i].cantidades } }, ...f.filas.slice(i + 1)] }));

  // ── Pulverización ──
  const setApp = (i: number, patch: Partial<PulvFormApp>) =>
    setPulv(a => a.map((x, j) => j === i ? { ...x, ...patch } : x));
  const setProd = (i: number, k: number, patch: Partial<PulvFormApp['productos'][number]>) =>
    setPulv(a => a.map((x, j) => j === i ? { ...x, productos: x.productos.map((p, m) => m === k ? { ...p, ...patch } : p) } : x));

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '1100px', width: '96vw' }}>
        <DialogBody>
          <DialogTitle>{isEdit ? pt.editMarcoTitle : pt.newMarcoTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '8px', maxHeight: '70vh', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                <Field label={pt.marcoName} required>
                  <Input value={nombre} placeholder={pt.marcoNamePlaceholder} onChange={(_, d) => setNombre(d.value)} disabled={busy} />
                </Field>
                <Field label={pt.fieldCrop}>
                  <Input value={crop} onChange={(_, d) => setCrop(d.value)} disabled={busy} />
                </Field>
                <Field label={pt.fieldCiclo}>
                  <Input value={ciclo} onChange={(_, d) => setCiclo(d.value)} disabled={busy} />
                </Field>
                <Field label={pt.marcoKind}>
                  <Dropdown
                    selectedOptions={[kind]}
                    value={kind === 'MARCO_FERTILIZACION' ? pt.typeMARCO_FERTILIZACION : pt.typeMARCO_PULVERIZACION}
                    onOptionSelect={(_, d) => setKind(d.optionValue as MarcoType)}
                    disabled={busy || isEdit}
                  >
                    <Option value="MARCO_FERTILIZACION">{pt.typeMARCO_FERTILIZACION}</Option>
                    <Option value="MARCO_PULVERIZACION">{pt.typeMARCO_PULVERIZACION}</Option>
                  </Dropdown>
                </Field>
              </div>

              {kind === 'MARCO_FERTILIZACION' ? (
                <>
                  <Text className={s.hint}>{pt.marcoFertHint}</Text>
                  <div className={s.scroll}>
                    <table className={s.table}>
                      <thead>
                        <tr>
                          <th className={s.th}>{pt.marcoTiempo}</th>
                          <th className={s.th}>{pt.marcoDias}</th>
                          <th className={s.th}>{pt.marcoTipoFert}</th>
                          {fert.columnas.map((c, i) => (
                            <th key={c.key} className={s.th}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <Input size="small" className={s.num} style={{ width: '110px' }} value={c.nombre} placeholder={pt.marcoColumnName}
                                  aria-label={pt.marcoColumnName} onChange={(_, d) => setCol(i, { nombre: d.value })} disabled={busy} />
                                <div style={{ display: 'flex', gap: '2px' }}>
                                  <Input size="small" style={{ width: '70px' }} value={c.unidad} aria-label={pt.marcoColumnUnit}
                                    onChange={(_, d) => setCol(i, { unidad: d.value })} disabled={busy} />
                                  <Button size="small" appearance="subtle" icon={<DeleteRegular />} aria-label={pt.marcoRemoveColumn} title={pt.marcoRemoveColumn}
                                    disabled={busy || fert.columnas.length === 1} onClick={() => removeCol(i)} />
                                </div>
                              </div>
                            </th>
                          ))}
                          <th className={s.th}>
                            <Button size="small" icon={<AddRegular />} disabled={busy}
                              onClick={() => setFert(f => ({ ...f, columnas: [...f.columnas, { key: newColumnKey(), nombre: '', unidad: DEFAULT_UNIT }] }))}>
                              {pt.marcoAddColumn}
                            </Button>
                          </th>
                          <th className={s.th}>{pt.marcoObs}</th>
                          <th className={s.th} />
                        </tr>
                      </thead>
                      <tbody>
                        {fert.filas.map((row, i) => (
                          <tr key={i}>
                            <td className={s.td}><Text>{i + 1}</Text></td>
                            <td className={s.td}>
                              <Input size="small" className={s.num} value={row.dias} aria-label={pt.marcoDias} inputMode="numeric"
                                onChange={(_, d) => setRow(i, { dias: d.value })} disabled={busy} />
                            </td>
                            <td className={s.td}>
                              <Input size="small" className={s.wide} value={row.tipo} aria-label={pt.marcoTipoFert}
                                onChange={(_, d) => setRow(i, { tipo: d.value })} disabled={busy} />
                            </td>
                            {fert.columnas.map(c => (
                              <td key={c.key} className={s.td}>
                                <Input size="small" style={{ width: '110px' }} inputMode="decimal" value={row.cantidades[c.key] ?? ''}
                                  aria-label={`${c.nombre || pt.marcoColumnName} · ${row.dias}`}
                                  onChange={(_, d) => setCell(i, c.key, d.value)} disabled={busy} />
                              </td>
                            ))}
                            <td className={s.td} />
                            <td className={s.td}>
                              <Input size="small" className={s.wide} value={row.observacion} aria-label={pt.marcoObs}
                                onChange={(_, d) => setRow(i, { observacion: d.value })} disabled={busy} />
                            </td>
                            <td className={s.td} style={{ whiteSpace: 'nowrap' }}>
                              <Button size="small" appearance="subtle" icon={<CopyRegular />} aria-label={pt.marcoDuplicateRow} title={pt.marcoDuplicateRow}
                                disabled={busy} onClick={() => duplicateRow(i)} />
                              <Button size="small" appearance="subtle" icon={<DeleteRegular />} aria-label={pt.marcoRemoveRow} title={pt.marcoRemoveRow}
                                disabled={busy || fert.filas.length === 1}
                                onClick={() => setFert(f => ({ ...f, filas: f.filas.filter((_, j) => j !== i) }))} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <Button size="small" icon={<AddRegular />} disabled={busy}
                      onClick={() => setFert(f => ({ ...f, filas: [...f.filas, emptyFertRow()] }))}>
                      {pt.marcoAddRow}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Text className={s.hint}>{pt.marcoPulvHint}</Text>
                  {pulv.map((app, i) => (
                    <div key={i} className={s.card}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr) auto', gap: '8px', alignItems: 'end' }}>
                        <Field label={pt.marcoDias}>
                          <Input value={app.dias} onChange={(_, d) => setApp(i, { dias: d.value })} disabled={busy} />
                        </Field>
                        <Field label={pt.marcoEtapa}>
                          <Input value={app.etapa} onChange={(_, d) => setApp(i, { etapa: d.value })} disabled={busy} />
                        </Field>
                        <Field label={pt.marcoObs}>
                          <Input value={app.observacion} onChange={(_, d) => setApp(i, { observacion: d.value })} disabled={busy} />
                        </Field>
                        <Button size="small" appearance="subtle" icon={<DeleteRegular />} aria-label={pt.removeApplication} title={pt.removeApplication}
                          disabled={busy || pulv.length === 1} onClick={() => setPulv(a => a.filter((_, j) => j !== i))} />
                      </div>
                      {app.productos.map((p, k) => (
                        <div key={k} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) auto', gap: '8px' }}>
                          <Input value={p.name} placeholder={pt.fieldProductName} aria-label={pt.fieldProductName}
                            onChange={(_, d) => setProd(i, k, { name: d.value })} disabled={busy} />
                          <Input value={p.dose} placeholder={pt.fieldDose} aria-label={pt.fieldDose}
                            onChange={(_, d) => setProd(i, k, { dose: d.value })} disabled={busy} />
                          <Button size="small" appearance="subtle" icon={<DeleteRegular />} aria-label={pt.removeProduct} title={pt.removeProduct}
                            disabled={busy || app.productos.length === 1}
                            onClick={() => setApp(i, { productos: app.productos.filter((_, m) => m !== k) })} />
                        </div>
                      ))}
                      <div>
                        <Button size="small" icon={<AddRegular />} disabled={busy}
                          onClick={() => setApp(i, { productos: [...app.productos, { name: '', dose: '' }] })}>
                          {pt.addProduct}
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div>
                    <Button size="small" icon={<AddRegular />} disabled={busy} onClick={() => setPulv(a => [...a, emptyPulvApp()])}>
                      {pt.addApplication}
                    </Button>
                  </div>
                </>
              )}

              {error && <Text role="alert" style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={busy}>{t.common.cancel}</Button>
            <Button appearance="primary" onClick={submit} disabled={busy} icon={busy ? <Spinner size="tiny" /> : undefined}>
              {pt.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
